/**
 * Where a signed-in session is kept: `localStorage` when the console
 * was launched as an installed app, `sessionStorage` in a browser tab.
 *
 * WHY IT IS A CHOICE AT ALL
 * -------------------------
 * Every console kept its session in `sessionStorage`, which a browser
 * discards when the tab closes and mobile browsers discard when they
 * evict a backgrounded tab -- which they do freely. On a desk that is
 * invisible; on a phone it means re-authenticating almost every time
 * you come back, which for someone answering an alert is the whole
 * interaction. And a home-screen app gets its own `sessionStorage`,
 * separate from the tab it was installed from, so installing the
 * console made the problem worse rather than better.
 *
 * THE DECISION (Akash, 2026-09-06): persist only when installed.
 * A shared front-desk tab is a browser tab and keeps the old
 * behaviour -- the session ends when it is closed, which is what you
 * want on a machine several people use. A device somebody has
 * deliberately installed the console onto is theirs, and stays signed
 * in until the token expires or they sign out.
 *
 * DECIDED ONCE, AT LOAD. `display-mode` can change while a page is
 * open (a desktop browser can move a page in and out of an installed
 * window), and a session that moved stores underneath a live tab would
 * read as an abrupt sign-out. The value is captured here and never
 * re-read.
 *
 * THE SUPPORT AND ADMIN CONSOLES DELIBERATELY DO NOT USE THIS. Neither
 * ships a manifest or a service worker, so neither can be installed and
 * `detectInstalled()` would be false in both -- the change would be
 * behaviour-neutral, and rewriting the session handling of two more
 * applications for no behaviour is not a trade worth making in the
 * codebase's most dangerous file. They keep `sessionStorage` directly.
 * If either gains a manifest, switch it here rather than copying this.
 *
 * SIGNING OUT CLEARS BOTH. The mode is a property of how the console
 * was OPENED, so the same person on the same device can legitimately
 * produce sessions in both stores -- installed one day, a tab the
 * next. Clearing only the active one would leave a token behind in the
 * other, which is the one outcome a sign-out must not have.
 */

const KEY = 'nexsoss:oidc-user-v1';

/**
 * True when the page is running as an installed application.
 *
 * `display-mode: standalone` covers Android and desktop installs and
 * the `minimal-ui` / `fullscreen` variants are checked alongside it,
 * because a manifest may ask for any of them. `navigator.standalone`
 * is the iOS-only predecessor, which Safari still uses for
 * Add to Home Screen and which reports nothing through `matchMedia`.
 */
function detectInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const displayModes = ['standalone', 'minimal-ui', 'fullscreen'];
    if (
      typeof window.matchMedia === 'function' &&
      displayModes.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches)
    ) {
      return true;
    }
    // iOS Safari. Not in the TypeScript DOM lib because it is not
    // standard, and absent in every other browser.
    return (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    // `matchMedia` with an unsupported feature throws in some engines.
    // A session that lives only as long as the tab is the safe answer.
    return false;
  }
}

const installed = detectInstalled();

/**
 * A Storage that never throws.
 *
 * Safari in private mode, and any browser with site data blocked,
 * throws on `setItem` rather than returning falsy -- inside the OIDC
 * callback that is an unhandled exception on the one code path a user
 * cannot retry without signing in again. Degrading to an in-memory
 * store keeps the session alive for the life of the page, which is
 * what those modes were going to give anyway.
 */
function safeStorage(store: Storage): Storage {
  try {
    const probe = '__nx_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch {
    return memoryStorage();
  }
}

function memoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear: () => map.clear(),
    getItem: (k: string) => map.get(k) ?? null,
    key: (i: number) => [...map.keys()][i] ?? null,
    removeItem: (k: string) => void map.delete(k),
    setItem: (k: string, v: string) => void map.set(k, v),
  } as Storage;
}

let resolved: Storage | null = null;

/** The store this session uses. Stable for the life of the page. */
export function authStore(): Storage {
  if (resolved) return resolved;
  resolved = safeStorage(installed ? window.localStorage : window.sessionStorage);
  return resolved;
}

/** True when the session will outlive the tab. Shown on the Profile screen. */
export function sessionPersists(): boolean {
  return installed;
}

/** Remove the stored session from BOTH stores. See the header. */
export function clearStoredSession(key: string = KEY): void {
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      store.removeItem(key);
    } catch {
      // Blocked site data: there was nothing stored to remove.
    }
  }
  try {
    authStore().removeItem(key);
  } catch {
    // The in-memory fallback cannot throw, but it is not worth
    // asserting that here.
  }
}
